import {
  Cancel,
  DeleteOutlined,
  Search,
} from "@mui/icons-material";
import { GridFilterListIcon } from "@mui/x-data-grid";
import {
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { GridFilterItem } from "@mui/x-data-grid-pro";
import { statusUIMapXero } from "shared";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { classNames } from "../../../common/lib/classNames";
import { useDebounce } from "../../hooks/useDebounce";
import { useGroups } from "../../hooks/useGroups";
import { useInvoiceTags } from "../../hooks/useInvoiceTags";
import { InvoiceTableFields, useInvoiceTableModels } from "../../hooks/useInvoices";

type FilterValueType = `string` | `number` | `date` | `boolean` | `status` | `group` | `invoiceTag`;

interface FilterFieldDefinition {
  field: string;
  label: string;
  valueType: FilterValueType;
  operators: Array<{ value: string; label: string }>;
}

const filterFieldDefinitions: FilterFieldDefinition[] = [
  {
    field: InvoiceTableFields.frozen,
    label: `Frozen`,
    valueType: `boolean`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.isLateFee,
    label: `Late Fee`,
    valueType: `boolean`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.invoiceSentStatus,
    label: `Sent`,
    valueType: `boolean`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.invoiceNumber,
    label: `Number`,
    valueType: `string`,
    operators: [{ value: `contains`, label: `contains` }],
  },
  {
    field: InvoiceTableFields.invoiceReference,
    label: `Reference`,
    valueType: `string`,
    operators: [{ value: `contains`, label: `contains` }],
  },
  {
    field: InvoiceTableFields.invoiceStatus,
    label: `Status`,
    valueType: `status`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.contactName,
    label: `Contact`,
    valueType: `string`,
    operators: [{ value: `contains`, label: `contains` }],
  },
  {
    field: InvoiceTableFields.primaryContactName,
    label: `Primary Contact`,
    valueType: `string`,
    operators: [{ value: `contains`, label: `contains` }],
  },
  {
    field: InvoiceTableFields.invoiceIssueDate,
    label: `Issue Date`,
    valueType: `date`,
    operators: [
      { value: `onOrAfter`, label: `on or after` },
      { value: `onOrBefore`, label: `on or before` },
    ],
  },
  {
    field: InvoiceTableFields.invoiceDueDate,
    label: `Due Date`,
    valueType: `date`,
    operators: [
      { value: `onOrAfter`, label: `on or after` },
      { value: `onOrBefore`, label: `on or before` },
    ],
  },
  {
    field: InvoiceTableFields.invoiceTotal,
    label: `Total`,
    valueType: `number`,
    operators: [
      { value: `>=`, label: `>=` },
      { value: `=`, label: `=` },
      { value: `<=`, label: `<=` },
    ],
  },
  {
    field: InvoiceTableFields.invoiceOutstanding,
    label: `Outstanding`,
    valueType: `number`,
    operators: [
      { value: `>=`, label: `>=` },
      { value: `=`, label: `=` },
      { value: `<=`, label: `<=` },
    ],
  },
  {
    field: InvoiceTableFields.daysOverdue,
    label: `Days Overdue`,
    valueType: `number`,
    operators: [
      { value: `>=`, label: `>=` },
      { value: `=`, label: `=` },
      { value: `<=`, label: `<=` },
    ],
  },
  {
    field: InvoiceTableFields.groupId,
    label: `Group`,
    valueType: `group`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.currency,
    label: `Currency`,
    valueType: `string`,
    operators: [{ value: `contains`, label: `contains` }],
  },
  {
    field: InvoiceTableFields.hasPaymentPlan,
    label: `Payment Plan`,
    valueType: `boolean`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.hasPaymentPromise,
    label: `Payment Promise`,
    valueType: `boolean`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.hasOpenQuery,
    label: `Query`,
    valueType: `boolean`,
    operators: [{ value: `is`, label: `is` }],
  },
  {
    field: InvoiceTableFields.invoiceTag,
    label: `Tag`,
    valueType: `invoiceTag`,
    operators: [{ value: `is`, label: `is` }],
  },
];

const fieldDefinitionByField = Object.fromEntries(
  filterFieldDefinitions.map(definition => [definition.field, definition]),
) as Record<string, FilterFieldDefinition>;

const booleanOptionsByField: Record<string, Array<{ label: string; value: boolean }>> = {
  [InvoiceTableFields.frozen]: [
    { label: `Frozen`, value: true },
    { label: `Not Frozen`, value: false },
  ],
  [InvoiceTableFields.isLateFee]: [
    { label: `Only Late Fees`, value: true },
    { label: `Late Fees Excluded`, value: false },
  ],
  [InvoiceTableFields.invoiceSentStatus]: [
    { label: `Sent`, value: true },
    { label: `Not Sent`, value: false },
  ],
  [InvoiceTableFields.hasPaymentPlan]: [
    { label: `Has Payment Plan`, value: true },
    { label: `No Payment Plan`, value: false },
  ],
  [InvoiceTableFields.hasPaymentPromise]: [
    { label: `Has Payment Promise`, value: true },
    { label: `No Payment Promise`, value: false },
  ],
  [InvoiceTableFields.hasOpenQuery]: [
    { label: `Has Open Query`, value: true },
    { label: `No Open Query`, value: false },
  ],
};

const statusOptions = Object.keys(statusUIMapXero).map(key => ({
  label: statusUIMapXero[key].name,
  value: key,
}));

function createFilterId() {
  return Math.round(Math.random() * 1e5);
}

function getDefaultOperator(field: string) {
  return fieldDefinitionByField[field]?.operators[0]?.value || `is`;
}

function getDefaultValue(field: string) {
  const definition = fieldDefinitionByField[field];

  if (!definition) {
    return ``;
  }

  if (definition.valueType === `boolean`) {
    return true;
  }

  if (definition.valueType === `status`) {
    return statusOptions[0]?.value || ``;
  }

  return ``;
}

function formatChipValue(
  item: GridFilterItem,
  groupLabelById: Record<string, string>,
) {
  const definition = fieldDefinitionByField[item.field];

  if (definition?.valueType === `boolean`) {
    const option = booleanOptionsByField[item.field]?.find(entry => entry.value === item.value);

    return option?.label ?? (item.value ? `Yes` : `No`);
  }

  if (definition?.valueType === `status`) {
    return statusUIMapXero[item.value as keyof typeof statusUIMapXero]?.name || String(item.value ?? ``);
  }

  if (definition?.valueType === `group`) {
    return groupLabelById[String(item.value)] || String(item.value ?? ``);
  }

  if (definition?.valueType === `date` && typeof item.value === `string`) {
    const asDate = new Date(item.value);

    if (!Number.isNaN(asDate.getTime())) {
      return asDate.toLocaleDateString();
    }
  }

  if (item.value === undefined || item.value === null || item.value === ``) {
    return ``;
  }

  return String(item.value);
}

export function InvoiceKanbanFilterBar() {
  const {
    filterModel,
    setFilterModel,
  } = useInvoiceTableModels();
  const { data: groupsRes } = useGroups();
  const { tags: invoiceTagsList } = useInvoiceTags();
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const quickSearchValue = filterModel.quickFilterValues?.[0] || ``;
  const [searchValue, setSearchValue] = useState(quickSearchValue);
  const debouncedSearchValue = useDebounce(searchValue, 350);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const groupOptions = useMemo(() => {
    return (groupsRes || []).map(group => ({
      label: group.title,
      value: group.id,
    }));
  }, [groupsRes]);

  const groupLabelById = useMemo(() => {
    return Object.fromEntries(groupOptions.map(option => [String(option.value), option.label]));
  }, [groupOptions]);

  const draftFilters = filterModel.items;
  const hasActiveSearch = Boolean(quickSearchValue.trim());
  const hasActiveFilters = draftFilters.length > 0 || hasActiveSearch;

  useEffect(() => {
    setSearchValue(quickSearchValue);
  }, [quickSearchValue]);

  useEffect(() => {
    const currentQuickSearch = filterModel.quickFilterValues?.[0] || ``;

    if (debouncedSearchValue === currentQuickSearch) {
      return;
    }

    setFilterModel({
      ...filterModel,
      quickFilterValues: debouncedSearchValue ? [debouncedSearchValue] : [],
    });
  }, [debouncedSearchValue, setFilterModel]);

  useEffect(() => {
    if (searchExpanded) {
      searchInputRef.current?.focus();
    }
  }, [searchExpanded]);

  function updateFilterItems(items: GridFilterItem[]) {
    setFilterModel({
      ...filterModel,
      items,
    });
  }

  function onAddFilter() {
    const firstField = filterFieldDefinitions[0].field;

    updateFilterItems([
      ...draftFilters,
      {
        id: createFilterId(),
        field: firstField,
        operator: getDefaultOperator(firstField),
        value: getDefaultValue(firstField),
      },
    ]);
  }

  function onUpdateFilter(filterId: GridFilterItem[`id`], patch: Partial<GridFilterItem>) {
    updateFilterItems(draftFilters.map(item => {
      if (item.id !== filterId) {
        return item;
      }

      return {
        ...item,
        ...patch,
      };
    }));
  }

  function onChangeFilterField(filterId: GridFilterItem[`id`], field: string) {
    onUpdateFilter(filterId, {
      field,
      operator: getDefaultOperator(field),
      value: getDefaultValue(field),
    });
  }

  function onRemoveFilter(filterId: GridFilterItem[`id`]) {
    updateFilterItems(draftFilters.filter(item => item.id !== filterId));
  }

  function onClearFilters() {
    setSearchValue(``);

    setFilterModel({
      ...filterModel,
      items: [],
      quickFilterValues: [],
    });
  }

  function renderValueInput(item: GridFilterItem) {
    const definition = fieldDefinitionByField[item.field];

    if (!definition) {
      return null;
    }

    if (definition.valueType === `boolean`) {
      return (
        <FormControl
          size={ `small` }
          sx={ { minWidth: 160 } }
        >
          <InputLabel>{ `Value` }</InputLabel>
          <Select
            label={ `Value` }
            value={ String(item.value) }
            onChange={ event => onUpdateFilter(item.id, {
              value: event.target.value === `true`,
            }) }
          >
            { (booleanOptionsByField[item.field] || []).map(option => (
              <MenuItem
                key={ String(option.value) }
                value={ String(option.value) }
              >
                { option.label }
              </MenuItem>
            )) }
          </Select>
        </FormControl>
      );
    }

    if (definition.valueType === `status`) {
      return (
        <FormControl
          size={ `small` }
          sx={ { minWidth: 160 } }
        >
          <InputLabel>{ `Value` }</InputLabel>
          <Select
            label={ `Value` }
            value={ item.value ?? `` }
            onChange={ event => onUpdateFilter(item.id, { value: event.target.value }) }
          >
            { statusOptions.map(option => (
              <MenuItem
                key={ option.value }
                value={ option.value }
              >
                { option.label }
              </MenuItem>
            )) }
          </Select>
        </FormControl>
      );
    }

    if (definition.valueType === `group`) {
      return (
        <FormControl
          size={ `small` }
          sx={ { minWidth: 160 } }
        >
          <InputLabel>{ `Value` }</InputLabel>
          <Select
            label={ `Value` }
            value={ item.value ?? `` }
            onChange={ event => onUpdateFilter(item.id, { value: event.target.value }) }
          >
            { groupOptions.map(option => (
              <MenuItem
                key={ option.value }
                value={ option.value }
              >
                { option.label }
              </MenuItem>
            )) }
          </Select>
        </FormControl>
      );
    }

    if (definition.valueType === `invoiceTag`) {
      return (
        <FormControl
          size={ `small` }
          sx={ { minWidth: 160 } }
        >
          <InputLabel>{ `Value` }</InputLabel>
          <Select
            label={ `Value` }
            value={ item.value ?? `` }
            onChange={ event => onUpdateFilter(item.id, { value: event.target.value }) }
          >
            { invoiceTagsList.map(tag => (
              <MenuItem
                key={ tag }
                value={ tag }
              >
                { tag }
              </MenuItem>
            )) }
          </Select>
        </FormControl>
      );
    }

    if (definition.valueType === `date`) {
      return (
        <TextField
          size={ `small` }
          label={ `Value` }
          type={ `date` }
          value={ typeof item.value === `string` ? item.value.slice(0, 10) : `` }
          onChange={ event => onUpdateFilter(item.id, { value: event.target.value }) }
          slotProps={ { inputLabel: { shrink: true } } }
          sx={ { minWidth: 160 } }
        />
      );
    }

    return (
      <TextField
        size={ `small` }
        label={ `Value` }
        type={ definition.valueType === `number` ? `number` : `text` }
        value={ item.value ?? `` }
        onChange={ event => {
          const nextValue = definition.valueType === `number`
            ? (event.target.value === `` ? `` : Number(event.target.value))
            : event.target.value;

          onUpdateFilter(item.id, { value: nextValue });
        } }
        sx={ { minWidth: 160 } }
      />
    );
  }

  return (
    <div className={ `mb-3` }>
      <div className={ `flex items-center gap-1` }>
        <Tooltip title={ `Filters` }>
          <IconButton
            ref={ filterButtonRef }
            size={ `small` }
            onClick={ () => setFilterPanelOpen(true) }
            aria-label={ `Open filters` }
            className={ classNames(
              draftFilters.length > 0 ? `text-paidniceGreen-700` : `text-slate-600`,
            ) }
          >
            <GridFilterListIcon fontSize={ `medium` } />
          </IconButton>
        </Tooltip>

        <div className={ `relative flex items-center` }>
          { !searchExpanded ? (
            <Tooltip title={ `Search` }>
              <IconButton
                size={ `small` }
                onClick={ () => setSearchExpanded(true) }
                aria-label={ `Search` }
                className={ classNames(
                  hasActiveSearch ? `text-paidniceGreen-700` : `text-slate-600`,
                ) }
              >
                <Search fontSize={ `small` } />
              </IconButton>
            </Tooltip>
          ) : (
            <TextField
              value={ searchValue }
              onChange={ event => setSearchValue(event.target.value) }
              onBlur={ () => {
                if (!searchValue) {
                  setSearchExpanded(false);
                }
              } }
              placeholder={ `Search number, contact or reference...` }
              size={ `small` }
              inputRef={ searchInputRef }
              sx={ {
                width: 280,
              } }
              slotProps={ { input: {
                startAdornment: (
                  <InputAdornment position={ `start` }>
                    <Search fontSize={ `small` } />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position={ `end` }>
                    <IconButton
                      size={ `small` }
                      aria-label={ `Clear search` }
                      onMouseDown={ event => event.preventDefault() }
                      onClick={ () => {
                        setSearchValue(``);
                        setSearchExpanded(false);
                      } }
                    >
                      <Cancel fontSize={ `small` } />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              } } }
            />
          ) }
        </div>

        { hasActiveFilters ? (
          <button
            type={ `button` }
            onClick={ onClearFilters }
            className={ `ml-1 cursor-pointer rounded-md px-2 py-1 text-xs font-medium text-paidniceGreen-700 transition-colors hover:bg-paidniceGreen-50` }
          >
            { `Clear filters` }
          </button>
        ) : null }
      </div>

      { draftFilters.length > 0 ? (
        <div className={ `mt-2 flex flex-wrap items-center gap-1.5` }>
          { draftFilters.map(item => {
            const definition = fieldDefinitionByField[item.field];
            const label = definition?.label || item.field;
            const operator = definition?.operators.find(entry => entry.value === item.operator)?.label
              || item.operator
              || `is`;
            const value = formatChipValue(item, groupLabelById);

            return (
              <Chip
                key={ item.id ?? `${item.field}-${item.operator}-${value}` }
                size={ `small` }
                label={ `${label} ${operator} ${value}`.trim() }
                onDelete={ () => onRemoveFilter(item.id) }
              />
            );
          }) }
        </div>
      ) : null }

      <Popover
        open={ filterPanelOpen }
        anchorEl={ filterButtonRef.current }
        onClose={ () => setFilterPanelOpen(false) }
        anchorOrigin={ {
          vertical: `bottom`,
          horizontal: `left`,
        } }
        transformOrigin={ {
          vertical: `top`,
          horizontal: `left`,
        } }
        slotProps={ {
          paper: {
            className: `mt-1 p-3 shadow-md`,
            sx: { minWidth: 640, maxWidth: `calc(100vw - 2rem)` },
          },
        } }
      >
        <div className={ `space-y-3` }>
          { draftFilters.length === 0 ? (
            <p className={ `text-sm text-slate-500` }>
              { `No filters applied.` }
            </p>
          ) : null }

          { draftFilters.map(item => {
            const definition = fieldDefinitionByField[item.field] || filterFieldDefinitions[0];

            return (
              <div
                key={ item.id ?? `${item.field}-${item.operator}` }
                className={ `flex flex-wrap items-center gap-2` }
              >
                <IconButton
                  size={ `small` }
                  aria-label={ `Remove filter` }
                  onClick={ () => onRemoveFilter(item.id) }
                >
                  <Cancel fontSize={ `small` } />
                </IconButton>

                <FormControl
                  size={ `small` }
                  sx={ { minWidth: 150 } }
                >
                  <InputLabel>{ `Columns` }</InputLabel>
                  <Select
                    label={ `Columns` }
                    value={ item.field }
                    onChange={ event => onChangeFilterField(item.id, String(event.target.value)) }
                  >
                    { filterFieldDefinitions.map(fieldDefinition => (
                      <MenuItem
                        key={ fieldDefinition.field }
                        value={ fieldDefinition.field }
                      >
                        { fieldDefinition.label }
                      </MenuItem>
                    )) }
                  </Select>
                </FormControl>

                <FormControl
                  size={ `small` }
                  sx={ { minWidth: 130 } }
                >
                  <InputLabel>{ `Operator` }</InputLabel>
                  <Select
                    label={ `Operator` }
                    value={ item.operator }
                    onChange={ event => onUpdateFilter(item.id, { operator: String(event.target.value) }) }
                  >
                    { definition.operators.map(operator => (
                      <MenuItem
                        key={ operator.value }
                        value={ operator.value }
                      >
                        { operator.label }
                      </MenuItem>
                    )) }
                  </Select>
                </FormControl>

                { renderValueInput(item) }
              </div>
            );
          }) }

          <div className={ `flex items-center justify-between gap-3 pt-1` }>
            <button
              type={ `button` }
              onClick={ onAddFilter }
              className={ `cursor-pointer text-sm font-semibold text-paidniceGreen-700 hover:text-paidniceGreen-800` }
            >
              { `+ ADD FILTER` }
            </button>
            <button
              type={ `button` }
              onClick={ onClearFilters }
              className={ `inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900` }
            >
              <DeleteOutlined fontSize={ `small` } />
              { `REMOVE ALL` }
            </button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
